/**
 * Authenticated calls into the cloud's app REST API (torik.ai).
 *
 * One implementation used by every cloud-facing service in the app: the token
 * comes from the auth store, a token that is about to expire is renewed first,
 * and a 401 mid-flight triggers one refresh + retry — so a long-lived page
 * survives an expired token without a sign-out.
 */

import { apiFetch } from './api';
import { getRuntimeConfig } from '../config/runtime';
import { useAuthStore } from '../stores/auth';
import { errorStatus } from '../utils/errors';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Extra request headers. The field app names the employee it works as
   * (`x-torik-employee`) — the cloud attributes the write to them.
   */
  headers?: Record<string, string>;
}

export async function cloudFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const authStore = useAuthStore();
  await authStore.ensureCloudToken();

  const send = (): Promise<T> => {
    const config = getRuntimeConfig();
    return apiFetch<T>(path, {
      method: options.method ?? 'GET',
      authToken: authStore.cloudAccessToken,
      baseUrl: config.cloudApiUrl,
      bypassRouting: true,
      ...(options.headers ? { headers: options.headers } : {}),
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  };

  try {
    return await send();
  } catch (error) {
    if (errorStatus(error) === 401) {
      const refreshed = await authStore.refreshCloudAccessToken();
      if (refreshed) return await send();
    }
    throw error;
  }
}

/**
 * POST a MULTIPART body to the cloud (used for file uploads).
 *
 * Separate from `cloudFetch` because that one is a JSON envelope: `apiFetch`
 * serialises the request (that is what lets hub traffic ride the WebRTC channel),
 * and a `FormData` cannot survive being stringified. So an upload goes direct to
 * the cloud's REST API — the same direct route `cloudFetch` takes with
 * `bypassRouting`, which is why this needs no second transport.
 *
 * The `Content-Type` header is deliberately NOT set: the browser must write it, or
 * the multipart boundary will be missing and the server will see no file.
 */
export async function cloudUpload<T>(path: string, formData: FormData): Promise<T> {
  const authStore = useAuthStore();
  await authStore.ensureCloudToken();

  const send = async (): Promise<Response> => {
    const config = getRuntimeConfig();
    return fetch(`${config.cloudApiUrl}${path}`, {
      method: 'POST',
      headers: authStore.cloudAccessToken
        ? { Authorization: `Bearer ${authStore.cloudAccessToken}` }
        : {},
      body: formData,
    });
  };

  let response = await send();
  if (response.status === 401) {
    const refreshed = await authStore.refreshCloudAccessToken();
    if (refreshed) response = await send();
  }

  if (!response.ok) {
    // Shape the failure like `cloudErrorMessage` reads it, so an upload's refusal
    // is shown with the server's own sentence.
    const error = (await response.json().catch(() => null)) as { error?: string } | null;
    throw {
      data: { error: error?.error ?? 'The upload failed' },
      message: error?.error ?? `The upload failed (${response.status})`,
    };
  }

  return (await response.json()) as T;
}

/** Surface a readable message from an API error (falls back to the message). */
export function cloudErrorMessage(error: unknown): string {
  const candidate = error as { data?: { error?: string; detail?: string }; message?: string };
  return candidate?.data?.error || candidate?.message || 'Something went wrong';
}
